import {
  Avatar,
  Box,
  Button,
  Center,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  ListItem,
  Spinner,
  Text,
  UnorderedList,
} from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { IdCard, Mail } from 'lucide-react';
import { useState, useCallback, ChangeEvent } from 'react';
import { useBookCoachingCall } from './useBookCoachingCall';
import { useDebounce } from 'use-debounce';
import {
  FormContentContainer,
  FormFooterContainer,
  FormMainContainer,
} from '@duke-hq/ui/components/Modals/FormModalLayouts';
import { trpcTenant } from '@duke-hq/app-provider-web/tenant/trpcTenant';
import type { RouterOutputs } from '@duke-hq/svc-api/provider';
import { FiSearch } from 'react-icons/fi/index.js';

function StudentList({
  students,
  onSelectStudent,
}: {
  students: RouterOutputs['users']['listStudentsWithEnrolments'] | undefined;
  onSelectStudent: (id: string) => void;
}) {
  if (!students) {
    return (
      <Box w="full">
        <Text color="fg.muted" textAlign="left" textStyle="sm">
          <Trans>Sorry, we couldn't load students. Please refresh or try again later.</Trans>
        </Text>
      </Box>
    );
  }

  if (students.length === 0) {
    return (
      <Box w="full">
        <Text color="fg.muted" textAlign="left" textStyle="sm">
          <Trans>No students found.</Trans>
        </Text>
      </Box>
    );
  }

  return (
    <UnorderedList styleType="none" w="full" p="0" m="0" maxH="400px" overflowY="auto">
      {students?.map((student) => {
        const fullName = `${student.first_name} ${student.last_name}`;
        return (
          <ListItem
            key={student.id}
            p="8px 12px"
            my="6px"
            border="1px solid"
            borderRadius="8px"
            borderColor="border.emphasis"
            cursor="pointer"
            _hover={{ bg: 'bg.muted' }}
            onClick={() => onSelectStudent(student.id)}
          >
            <HStack align="center" spacing="4">
              <Avatar size="sm" name={fullName} src={student?.picture_url ?? undefined} />
              <Text fontWeight="medium">{fullName}</Text>
              {student?.studentData?.universal_student_identifier ? (
                <HStack align="center" spacing="1" gap={2}>
                  <Icon as={IdCard} boxSize="16px" />
                  <Text fontSize="sm" lineHeight="1.25" color="fg.muted">
                    {student.studentData.universal_student_identifier ?? 'N/A'}
                  </Text>
                </HStack>
              ) : null}
              <HStack align="center" spacing="1" gap={2}>
                <Icon as={Mail} boxSize="16px" />
                <Text fontSize="sm" lineHeight="1.25" color="fg.muted">
                  {student.email}
                </Text>
              </HStack>
            </HStack>
          </ListItem>
        );
      })}
    </UnorderedList>
  );
}

export function SelectStudent({ goToNextStep }: { goToNextStep: () => void }) {
  const { setSelectedStudent, resetStates, closeModal } = useBookCoachingCall();

  const [search, setSearch] = useState<string>('');
  const [debouncedSearch] = useDebounce(search, 500);

  const { data: students, isLoading } = trpcTenant.users.listStudentsWithEnrolments.useQuery({
    search: debouncedSearch,
    excludeInternalEnrolments: true,
  });

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const q = value.trim();
    setSearch(q);
  };

  const handleSelectStudent = useCallback(
    (id: string) => {
      const student = students?.find((s) => s.id === id);
      if (!student) {
        return;
      }

      setSelectedStudent({
        id: student.id,
        firstName: student.first_name,
        lastName: student.last_name ?? '',
        email: student.email ?? '',
        picture_url: student.picture_url ?? '',
        universal_student_identifier: student?.studentData?.universal_student_identifier ?? '',
      });

      goToNextStep();
    },
    [students, setSelectedStudent],
  );

  const handleCancel = useCallback(() => {
    resetStates();
    closeModal();
  }, [resetStates, closeModal]);

  return (
    <FormMainContainer>
      <FormContentContainer>
        <Box alignSelf="flex-start">
          <Text fontSize="md" fontWeight="medium">
            <Trans>Student</Trans>
          </Text>
        </Box>
        <InputGroup size="md">
          <InputLeftElement pointerEvents="none">
            <Icon as={FiSearch} color="fg.muted" boxSize="16px" />
          </InputLeftElement>
          <Input
            autoComplete="off"
            type="search"
            autoFocus
            size="md"
            onChange={handleInputChange}
            placeholder="Search"
            value={search}
            width="full"
          />
        </InputGroup>
        {isLoading ? (
          <Center h="full" w="full">
            <Spinner color="border.accent" size="lg" />
          </Center>
        ) : (
          <StudentList students={students} onSelectStudent={handleSelectStudent} />
        )}
      </FormContentContainer>
      <FormFooterContainer>
        <Button onClick={handleCancel} variant="secondary">
          <Trans>Cancel</Trans>
        </Button>
      </FormFooterContainer>
    </FormMainContainer>
  );
}
